import {isNullOrEmpty} from 'shared/collection'
import {zx} from 'shared/type'
import {Sb} from 'utils/db'
import {z} from 'zod'
import {FetchProfileProps, MyProfile, zProfileRow, zProfileWorkspaceCfg, zProfileLicense} from './profileTypes'
import {type Permission, profileHasPermission} from './permission'
import {doSafe, err, ok, type Err, type OK} from 'shared/promise'
import type {Role} from 'shared/forecast/roleTypes'
import {env} from 'utils/env'
import {isLicenseExpired} from 'shared/forecast/license'
import {match} from 'ts-pattern'
import {uri} from 'shared/uri'

const zGetProfileRowError = z.object({error: z.string(), workspace_id: z.string()})
type GetProfileError = z.infer<typeof zGetProfileRowError>
const zGetProfileRowOk = zProfileRow
  .extend({is_new: z.boolean().default(false), last_activity: zx.nullish.string})
  .omit({deleted: true, updated_at: true})

// const zGetProfileRow = z.union([zGetProfileRowError, zGetProfileRowOk])

export async function fetchProfiles(
  sb: Sb,
  props?: FetchProfileProps
): Promise<undefined | (OK<MyProfile & {last_activity?: string}> | Err<GetProfileError>)[]> {
  const {data, error} = await sb.rpc('get_profiles', {
    invite_workspace: props?.invite?.workspace,
    invite_id: props?.invite?.id
  })
  if (error) throw error
  if (isNullOrEmpty(data)) return undefined
  return data.map(d =>
    match(d)
      .when(
        d => 'error' in d && d.error != null,
        r => err(zGetProfileRowError.parse(r))
      )
      .otherwise(d => {
        const {cfg, ...rowProps} = zGetProfileRowOk.parse(d) // drop email, always get from user
        const hasRole = (role: Role) => cfg.roles?.includes(role) ?? false
        const hasPermission = (p: Permission) => profileHasPermission(cfg, p)
        const workspace = zProfileWorkspaceCfg.parse(d.workspace_cfg)
        const license = doSafe(d.license_cfg, l => zProfileLicense.parse(l))
        const readonly = license ? isLicenseExpired({...license, isSelfHost: env.NEXT_PUBLIC_SELF_HOST}) : false
        return ok({...cfg, ...rowProps, workspace, hasRole, hasPermission, license, readonly} as MyProfile)
      })
  )
}

export const profileAvatarAbsolute = (url: string) => {
  let u = uri(url)
  if (!u.protocol && !u.host) u = uri(env.URL).with({path: u.path ?? []})
  return u.url
}

export const avatarNames = {
  bear: [
    'Chilli Bear',
    'Arctic Bear',
    'Honey Bear',
    'Fuzzy Bear',
    'Polar Bear',
    'Grizzly Bear',
    'Berry Bear',
    'Black Bear',
    'Moon Bear',
    'Sun Bear'
  ],
  bee: [
    'Honey Buzz',
    'Bumble Buzz',
    'Stripe Buzz',
    'Nectar Buzz',
    'Queen Bee',
    'Dance Bee',
    'Worker Bee',
    'Drone Bee',
    'Garden Bee',
    'Wild Bee'
  ],
  bird: [
    'Blue Jay',
    'Red Robin',
    'Golden Finch',
    'Silent Owl',
    'Swift Hawk',
    'Dawn Sparrow',
    'Twilight Thrush',
    'Mystic Raven',
    'Eagle Eye',
    'Sky Lark'
  ],
  butterfly: [
    'Petal Wings',
    'Monarch Flutter',
    'Silky Wings',
    'Garden Dance',
    'Moth Whisper',
    'Sunny Flutter',
    'Emerald Flyer',
    'Twilight Swirl',
    'Meadow Breeze',
    'Raindrop Skimmer'
  ],
  chameleon: [
    'Vibrant Cam',
    'Rainbow Hide',
    'Stealth Scale',
    'Jungle Mingle',
    'Desert Blend',
    'Tree Hugger',
    'Rocky Camo',
    'Leafy Suit',
    'Night Fade',
    'Sunset Shift'
  ],
  chick: [
    'Sunny Waddles',
    'Peep Peep',
    'Golden Fluff',
    'Little Tweet',
    'Egg Hopper',
    'Soft Down',
    'Spring Peck',
    'Barnyard Dash',
    'Meadow Run',
    'Cozy Nest'
  ],
  chicken: [
    'Little Chicken',
    'Feather Peck',
    'Barn Boss',
    'Egg Layer',
    'Cluck Cluck',
    'Golden Comb',
    'Red Rooster',
    'Dust Bather',
    'Sunrise Caller',
    'Corn Runner'
  ],
  crab: [
    'Crabby Pincer',
    'Sandy Claw',
    'Rock Dweller',
    'Shore Scuttler',
    'Tide Rider',
    'Moon Walker',
    'Sunset Snapper',
    'Beach Comber',
    'Shell Fighter',
    'Wave Dancer'
  ],
  crocodile: [
    'Bored Snout',
    'Swamp King',
    'River Jaws',
    'Muddy Hunter',
    'Silent Drifter',
    'Night Stalker',
    'Marsh Lurker',
    'Log Camo',
    'Reef Ambush',
    'Jungle Guard'
  ],
  deer: [
    'Bambi Rednose',
    'Forest Stag',
    'Meadow Grazer',
    'Twilight Antler',
    'Gentle Fawn',
    'Oak Wanderer',
    'Misty Doe',
    'River Jump',
    'Snow Tracker',
    'Golden Buck'
  ],
  dolphin: [
    'Wave Rider',
    'Ocean Dancer',
    'Sea Spark',
    'Sunset Jumper',
    'Reef Racer',
    'Deep Diver',
    'Star Flipper',
    'Moon Glide',
    'Tide Sprinter',
    'Coral Explorer'
  ],
  ducky: [
    'Quacky Puddle',
    'Pond Waddler',
    'Rain Dancer',
    'Stream Sailor',
    'Lake Lounger',
    'Mud Diver',
    'Bubble Quack',
    'Wet Feather',
    'Splash Mate',
    'Bill Shaker'
  ],
  eagle: [
    'Angry Eagle',
    'Sky Ruler',
    'Mountain Soar',
    'Wing Beat',
    'Storm Hunter',
    'Talon Grip',
    'Windy Glider',
    'Sunset Watcher',
    'Horizon Seeker',
    'Cloud Surfer'
  ],
  elephant: [
    'Heavy Tusk',
    'Gentle Giant',
    'Forest Walker',
    'Savanna Trumpet',
    'Mud Wallow',
    'Leaf Eater',
    'River Splash',
    'Tusk Shine',
    'Dust Roller',
    'Path Maker'
  ],
  frog: [
    'Lily Hopper',
    'Pond Skimmer',
    'Mud Leaper',
    'Green Croaker',
    'Night Singer',
    'Rain Caller',
    'Swamp Jumper',
    'Bug Snatcher',
    'Water Dancer',
    'Leaf Lurker'
  ],
  fox: [
    'Jordan Prowl',
    'Forest Sneak',
    'Snow Tracker',
    'Dune Stalker',
    'Night Whisper',
    'Field Scout',
    'Red Brush',
    'Moon Howler',
    'Sunset Watch',
    'Berry Thief'
  ],
  giraffe: [
    'Skyler Reach',
    'Leaf Nibbler',
    'Tall Walker',
    'Savanna Watcher',
    'Sunset Spotter',
    'Tree Muncher',
    'Horizon Gazer',
    'Shade Seeker',
    'Cloud Napper',
    'Star Grazer'
  ],
  goose: [
    'Raging Questioner',
    'Honk Leader',
    'Feather Flapper',
    'Pond Sailor',
    'Sky Migrant',
    'Grass Nibbler',
    'Flock Caller',
    'Wing Racer',
    'Water Dipper',
    'Sun Chaser'
  ],
  'guinea-pig': [
    'Finley Whisker',
    'Fluffy Nugget',
    'Tiny Squeaker',
    'Furry Muncher',
    'Snuggle Puff',
    'Whisker Twitch',
    'Cuddle Bug',
    'Wheezy Nibbler',
    'Cozy Burrower',
    'Silky Soft'
  ],
  hippo: [
    'River Bulk',
    'Mud Bath',
    'Water Giant',
    'Sunset Yawn',
    'Reed Hider',
    'Bank Sleeper',
    'Lake Warden',
    'River Horse',
    'Flood King',
    'Grass Grazer'
  ],
  horse: [
    'Longface Sean',
    'Gallop King',
    'Meadow Trot',
    'Stallion Pride',
    'Hoof Runner',
    'Mane Shaker',
    'Pasture Bound',
    'Dusk Neigh',
    'Trail Blazer',
    'Fence Jumper'
  ],
  ibis: [
    'Sharron Longbeak',
    'Marsh Stalker',
    'Water Strider',
    'Mud Prober',
    'Nest Weaver',
    'Reed Dancer',
    'Sunset Forager',
    'Wing Stretch',
    'Tide Watcher',
    'Sand Walker'
  ],
  kangaroo: [
    'Grumpy Pouch',
    'Outback Hopper',
    'Red Leaper',
    'Dune Bouncer',
    'Bush Tail',
    'Joey Carrier',
    'Grass Boxer',
    'Desert Runner',
    'Shade Seeker',
    'Creek Jumper'
  ],
  kitty: [
    'Ginger Tome',
    'Whisker Purr',
    'Furball Chase',
    'Silent Meow',
    'Night Stalk',
    'Sun Patch',
    'Lap Napper',
    'Mouse Catcher',
    'Window Watch',
    'Shadow Lurker'
  ],
  koala: [
    'Leaf Chomper',
    'Eucalyptus Dreamer',
    'Tree Hugger',
    'Bark Sleeper',
    'Nose Twitch',
    'Branch Mover',
    'Leaf Picker',
    'Gum Nibbler',
    'Twilight Gazer',
    'Forest Snoozer'
  ],
  lemur: [
    'Rascal Marco',
    'Tree Dancer',
    'Tail Swisher',
    'Fruit Snatcher',
    'Jungle Jumper',
    'Canopy Runner',
    'Night Scurry',
    'Leaf Looker',
    'Vine Swinger',
    'Eye Gleamer'
  ],
  lion: [
    'Golden Mane',
    'Savanna King',
    'Pride Leader',
    'Sunset Roar',
    'Grass Stalker',
    'Cub Protector',
    'Territory Guard',
    'Dust Ruler',
    'Mane Fluff',
    'Hunt Strategist'
  ],
  lobster: [
    'Claw Snapper',
    'Deep Pincher',
    'Rock Hider',
    'Tide Scuttler',
    'Shell Fighter',
    'Blue Hunter',
    'Sand Burrower',
    'Reef Lurker',
    'Wave Wrestler',
    'Ocean Blue'
  ],
  meerkat: [
    'Dune Scout',
    'Sand Watcher',
    'Desert Lookout',
    'Burrow Digger',
    'Sun Bathe',
    'Termite Eater',
    'Clan Caller',
    'Dust Runner',
    'Twilight Guard',
    'Moon Gazer'
  ],
  monkey: [
    'Naughty Monkey',
    'Tree Swinger',
    'Banana Thief',
    'Jungle Mischief',
    'Branch Jumper',
    'Fruit Snatch',
    'Canopy Call',
    'Leaf Bed',
    'Vine Tumbler',
    'Forest Giggler'
  ],
  mouse: [
    'Cheese Nibbler',
    'Silent Sneak',
    'Whisker Twitch',
    'Grain Stasher',
    'Night Scurry',
    'Nest Builder',
    'Soft Patter',
    'Wall Squeak',
    'Crumbs Finder',
    'Dark Dash'
  ],
  octopus: [
    'Quinn Ink',
    'Deep Mystery',
    'Tentacle Stretch',
    'Coral Camo',
    'Sand Swimmer',
    'Night Glow',
    'Wave Crawler',
    'Shell Collector',
    'Ocean Drifter',
    'Tide Hugger'
  ],
  ostrich: [
    'Casey Dash',
    'Sand Sprinter',
    'Feather Runner',
    'Neck Stretch',
    'Sun Shade',
    'Egg Guard',
    'Dune Watcher',
    'Dust Kick',
    'Grain Peck',
    'Horizon Stare'
  ],
  owl: [
    'Rowan Shadow',
    'Night Hunter',
    'Silent Flight',
    'Moon Watch',
    'Forest Whisper',
    'Twilight Glide',
    'Star Gaze',
    'Branch Perch',
    'Prey Spot',
    'Wing Hush'
  ],
  panda: [
    'Bamboo Master',
    'Leaf Eater',
    'Tree Climber',
    'Fur Roll',
    'Cub Play',
    'Stump Sit',
    'Forest Munch',
    'Branch Rest',
    'Grove Nap',
    'Meadow Roam'
  ],
  penguin: [
    'Ice Slide',
    'Snow Waddle',
    'Chill Dive',
    'Glacier Hop',
    'Wave Rider',
    'Frost March',
    'Huddle Shuffle',
    'Blizzard Peek',
    'Krill Hunt',
    'Nest Cuddle'
  ],
  pig: [
    'Slouching Pinky',
    'Mud Roller',
    'Snout Root',
    'Barn Snuggle',
    'Fence Jumper',
    'Trough Eater',
    'Slop Splash',
    'Sun Bath',
    'Field Trot',
    'Grass Sniffer'
  ],
  puppy: [
    'Buddy Paws',
    'Waggy Tail',
    'Furry Friend',
    'Park Runner',
    'Bone Digger',
    'Sock Thief',
    'Cuddle Bug',
    'Puddle Splash',
    'Door Wait',
    'Ball Chase'
  ],
  rabbit: [
    'Cotton Tail',
    'Hop Skip',
    'Carrot Nibbler',
    'Burrow Digger',
    'Field Bounce',
    'Fluff Shake',
    'Leaf Munch',
    'Moonlight Leap',
    'Dew Sniff',
    'Meadow Bound'
  ],
  seal: [
    'Neil the Seal',
    'Ocean Clap',
    'Beach Flop',
    'Wave Glide',
    'Rock Lounger',
    'Fish Catch',
    'Tide Play',
    'Sand Nap',
    'Ice Slide',
    'Sun Bathe'
  ],
  shark: [
    'Jawsome Joe',
    'Deep Hunter',
    'Blue Terror',
    'Wave Stalker',
    'Reef Lurker',
    'Gulf Swim',
    'Fin Flash',
    'Ocean Roam',
    'Tide Shadow',
    'Night Thrash'
  ],
  sheep: [
    'Fluffy Meadow',
    'Wool Wander',
    'Grass Graze',
    'Fence Jump',
    'Hill Sleep',
    'Barn Snuggle',
    'Shear Baa',
    'Meadow Munch',
    'Cloud Fluff',
    'Sunset Bleat'
  ],
  sloth: [
    'Slowpoke Cedar',
    'Tree Yawn',
    'Leaf Grab',
    'Branch Nap',
    'Jungle Drape',
    'Canopy Crawl',
    'Fruit Reach',
    'Rain Drip',
    'Moss Hang',
    'Forest Watch'
  ],
  snail: [
    'Slimey Trail',
    'Shell Drag',
    'Garden Glide',
    'Leaf Climb',
    'Dew Slick',
    'Night Creep',
    'Moss Crawl',
    'Rock Slide',
    'Mud Stick',
    'Petal Rest'
  ],
  snake: [
    'Happy Scales',
    'Sly Slither',
    'Grass Hiss',
    'Sun Coil',
    'Desert Glide',
    'Rock Hide',
    'Leaf Lurk',
    'Marsh Strike',
    'Sand Crawl',
    'Tree Climb'
  ],
  'snow-macaque': [
    'Frosty Macaque',
    'Ice Climb',
    'Snow Forage',
    'Glacier Play',
    'Frost Leap',
    'Huddle Warm',
    'Blizzard Peek',
    'Snowball Fight',
    'Ice Groom',
    'Chill Roam'
  ],
  squirrel: [
    'Nutty Hoarder',
    'Tree Leap',
    'Acorn Stash',
    'Branch Run',
    'Leaf Nest',
    'Fence Sprint',
    'Garden Raid',
    'Twig Snatch',
    'Bark Scramble',
    'Sky Dancer'
  ],
  swift: [
    'Taytay Swift',
    'Wing Zoom',
    'Sky Swoop',
    'Cloud Dive',
    'Breeze Rider',
    'Sunset Dash',
    'Horizon Zoom',
    'Star Chase',
    'Moon Glide',
    'Wind Whisper'
  ],
  turtle: [
    'Shelly McShellface',
    'Reef Hide',
    'Sand Dig',
    'Sun Bask',
    'Wave Ride',
    'Shell Tuck',
    'Beach Crawl',
    'Seagrass Snack',
    'Tide Wait',
    'Ocean Wander'
  ],
  unicorn: [
    'Rainbow Horn',
    'Magic Gallop',
    'Stardust Trail',
    'Moonbeam Shine',
    'Glitter Leap',
    'Mystic Neigh',
    'Enchanted Prance',
    'Crystal Graze',
    'Fairy Whinny',
    'Dream Canter'
  ],
  whale: [
    'Krill Hoover',
    'Deep Song',
    'Ocean Giant',
    'Wave Rider',
    'Moon Breach',
    'Star Dive',
    'Fog Spout',
    'Tide Surge',
    'Glacier Watch',
    'Arctic Float'
  ]
}
